import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Loader2, Users, UserPlus, Play, CheckCircle2, 
  Calendar, Target, User, Sparkles, Clock, Award, Mail, Copy, ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Expertise areas that align with IF personas
const EXPERTISE_AREAS = [
  { value: 'technology', label: 'Technology & Innovation' },
  { value: 'social-impact', label: 'Social Impact & Policy' },
  { value: 'design', label: 'Design & User Experience' },
  { value: 'research', label: 'Research & Analysis' },
  { value: 'business', label: 'Business & Strategy' },
  { value: 'sustainability', label: 'Sustainability & Environment' },
  { value: 'healthcare', label: 'Healthcare & Wellbeing' },
  { value: 'education', label: 'Education & Learning' },
  { value: 'community', label: 'Community Building' },
  { value: 'other', label: 'Other Expertise' }
];

const ImpactLabDashboard = () => {
  const { labId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [impactLab, setImpactLab] = useState(null);
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviting, setInviting] = useState(false);
  const [starting, setStarting] = useState(false);
  
  // Email invite state
  const [showEmailInvite, setShowEmailInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteExpertise, setInviteExpertise] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendingEmailInvite, setSendingEmailInvite] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState(null);

  useEffect(() => {
    fetchImpactLab();
  }, [labId]);

  const fetchImpactLab = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/impact-lab/${labId}`);
      setImpactLab(response.data.impact_lab);
    } catch (error) {
      console.error('Error fetching Impact Lab:', error);
      alert('Failed to load Impact Lab');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteUsername.trim()) return;

    try {
      setInviting(true);
      await axios.post(
        `${BACKEND_URL}/api/impact-lab/${labId}/invite?invitee_username=${inviteUsername}`,
        {},
        { withCredentials: true }
      );
      
      toast({
        title: "Invitation Sent!",
        description: `Invitation sent to @${inviteUsername}`
      });
      setInviteUsername('');
      fetchImpactLab(); // Refresh to show updated team
    } catch (error) {
      console.error('Error inviting user:', error);
      toast({
        title: "Invitation Failed",
        description: error.response?.data?.detail || 'Failed to send invitation',
        variant: "destructive"
      });
    } finally {
      setInviting(false);
    }
  };

  const handleEmailInvite = async () => {
    if (!inviteEmail.trim() || !inviteExpertise) {
      toast({
        title: "Missing Information",
        description: "Please enter an email and select an expertise area",
        variant: "destructive"
      });
      return;
    }

    try {
      setSendingEmailInvite(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/impact-lab/${labId}/invite-by-email`,
        {
          email: inviteEmail,
          expertise_area: EXPERTISE_AREAS.find(e => e.value === inviteExpertise)?.label || inviteExpertise,
          custom_message: inviteMessage || null
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        setLastInviteLink(response.data.invite_url);
        toast({
          title: "Invitation Sent! 🎉",
          description: response.data.email_sent 
            ? `Email sent to ${inviteEmail}` 
            : `Invitation created. Share the link below with ${inviteEmail}`
        });
        
        // Clear form but keep showing link
        setInviteEmail('');
        setInviteExpertise('');
        setInviteMessage('');
      }
    } catch (error) {
      console.error('Error sending email invite:', error);
      toast({
        title: "Invitation Failed",
        description: error.response?.data?.detail || 'Failed to send invitation',
        variant: "destructive"
      });
    } finally {
      setSendingEmailInvite(false);
    }
  };

  const copyInviteLink = () => {
    if (lastInviteLink) {
      navigator.clipboard.writeText(lastInviteLink);
      toast({
        title: "Link Copied!",
        description: "Share this link with your invitee"
      });
    }
  };

  const handleStartSprint = async () => {
    try {
      setStarting(true);
      await axios.post(
        `${BACKEND_URL}/api/impact-lab/${labId}/start`,
        {},
        { withCredentials: true }
      );
      
      // Refresh impact lab data
      await fetchImpactLab();
      alert('Impact Lab sprint started! 🚀');
    } catch (error) {
      console.error('Error starting sprint:', error);
      alert(error.response?.data?.detail || 'Failed to start sprint');
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
      </div>
    );
  }

  const teamSize = impactLab?.team_member_details?.length || 0;
  const minSize = impactLab?.team_size_min || 6;
  const maxSize = impactLab?.team_size_max || 12;
  const canStart = teamSize >= minSize && impactLab?.status === 'forming' && impactLab?.creator_id === user?.id;
  const isActive = impactLab?.status === 'active';
  const isCompleted = impactLab?.status === 'completed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(`/challenges/${impactLab?.challenge_id}`)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Challenge
            </Button>
            <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Impact Lab Dashboard
            </h1>
            <div className="w-32"></div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-6xl">
        {/* Challenge Title */}
        <Card className="mb-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">{impactLab?.challenge_title}</h2>
                <div className="flex items-center gap-4 text-sm opacity-90">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {isActive ? `Day ${impactLab?.current_day} of 7` : 
                       isCompleted ? 'Completed' : 'Forming Team'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{teamSize}/{maxSize} members</span>
                  </div>
                </div>
              </div>
              {impactLab?.status === 'forming' && (
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
                  🔨 Forming
                </span>
              )}
              {isActive && (
                <span className="px-3 py-1 bg-green-500/20 rounded-full text-sm">
                  ✨ Active
                </span>
              )}
              {isCompleted && (
                <span className="px-3 py-1 bg-blue-500/20 rounded-full text-sm">
                  🏆 Completed
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Status Card */}
            {!isActive && !isCompleted && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    Team Formation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Team Progress</span>
                      <span className="text-sm font-semibold text-purple-600">
                        {teamSize} / {minSize} minimum
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all ${
                          teamSize >= minSize ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-purple-500 to-pink-500'
                        }`}
                        style={{ width: `${Math.min((teamSize / minSize) * 100, 100)}%` }}
                      />
                    </div>
                  </div>

                  {teamSize < minSize ? (
                    <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-4">
                      <p className="text-sm text-orange-800">
                        <strong>Need {minSize - teamSize} more member{minSize - teamSize !== 1 ? 's' : ''}!</strong> 
                        <br />Invite Fountainheads with relevant expertise to start your 7-day IDEO sprint.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
                      <p className="text-sm text-green-800">
                        <strong>Team ready to launch! 🎉</strong>
                        <br />You have enough members to start the 7-day Impact Lab sprint.
                      </p>
                    </div>
                  )}

                  {canStart && (
                    <Button
                      onClick={handleStartSprint}
                      disabled={starting}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-lg py-6"
                    >
                      {starting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Starting Sprint...
                        </>
                      ) : (
                        <>
                          <Play className="w-5 h-5 mr-2" />
                          Start 7-Day Impact Lab Sprint
                        </>
                      )}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Active Sprint Progress */}
            {isActive && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    Current Sprint Progress
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Sprint Progress</span>
                      <span className="text-sm font-semibold text-purple-600">
                        Day {impactLab?.current_day} of 7
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                        style={{ width: `${(impactLab?.current_day / 7) * 100}%` }}
                      />
                    </div>
                  </div>

                  {impactLab?.roadmap && impactLab.roadmap[impactLab.current_day - 1] && (
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border-l-4 border-purple-500">
                      <div className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-1">
                        {impactLab.roadmap[impactLab.current_day - 1].ideo_phase}
                      </div>
                      <h4 className="font-bold text-lg mb-2">
                        {impactLab.roadmap[impactLab.current_day - 1].title}
                      </h4>
                      <div className="text-sm text-gray-700">
                        <strong>Today's Objectives:</strong>
                        <ul className="mt-1 space-y-1">
                          {impactLab.roadmap[impactLab.current_day - 1].objectives.slice(0, 3).map((obj, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                              <span>{obj}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={() => navigate(`/impact-lab/${labId}/workspace`)}
                    className="w-full mt-4 bg-gradient-to-r from-purple-600 to-pink-600"
                  >
                    Open Workspace
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Completed */}
            {isCompleted && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-600" />
                    Sprint Completed!
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Award className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">Congratulations! 🎉</h3>
                    <p className="text-gray-600 mb-6">
                      Your team completed the 7-day Impact Lab sprint using IDEO's design thinking methodology.
                    </p>
                    <Button className="bg-gradient-to-r from-purple-600 to-pink-600">
                      <Award className="w-4 h-4 mr-2" />
                      Download Certificate
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Team Members */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  Team Members ({teamSize})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  {impactLab?.team_member_details?.map((member) => (
                    <div key={member.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      {member.profile_picture_url ? (
                        <img
                          src={member.profile_picture_url}
                          alt={member.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{member.name}</div>
                        <div className="text-xs text-gray-500 truncate">@{member.username}</div>
                      </div>
                      {member.id === impactLab?.creator_id && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Creator</span>
                      )}
                    </div>
                  ))}
                </div>

                {impactLab?.status === 'forming' && teamSize < maxSize && (
                  <div className="space-y-4">
                    {/* Username Invite */}
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-2">Invite by Username</div>
                      <div className="flex gap-2">
                        <Input
                          value={inviteUsername}
                          onChange={(e) => setInviteUsername(e.target.value)}
                          placeholder="@username"
                          onKeyPress={(e) => e.key === 'Enter' && handleInvite()}
                        />
                        <Button
                          onClick={handleInvite}
                          disabled={inviting || !inviteUsername.trim()}
                          size="sm"
                        >
                          {inviting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <UserPlus className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-gray-500">or</span>
                      </div>
                    </div>

                    {/* Email Invite */}
                    <div>
                      <Button
                        variant={showEmailInvite ? "secondary" : "outline"}
                        size="sm"
                        className="w-full"
                        onClick={() => setShowEmailInvite(!showEmailInvite)}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Invite by Email (New Users)
                      </Button>

                      {showEmailInvite && (
                        <div className="mt-3 p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
                          <Input
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="friend@email.com"
                            className="bg-white"
                          />
                          
                          <select
                            value={inviteExpertise}
                            onChange={(e) => setInviteExpertise(e.target.value)}
                            className="w-full px-3 py-2 border rounded-md text-sm bg-white"
                          >
                            <option value="">Select their expertise area...</option>
                            {EXPERTISE_AREAS.map(area => (
                              <option key={area.value} value={area.value}>
                                {area.label}
                              </option>
                            ))}
                          </select>

                          <Textarea
                            value={inviteMessage}
                            onChange={(e) => setInviteMessage(e.target.value)}
                            placeholder="Add a personal message (optional)"
                            className="bg-white text-sm"
                            rows={2}
                          />

                          <Button
                            onClick={handleEmailInvite}
                            disabled={sendingEmailInvite || !inviteEmail.trim() || !inviteExpertise}
                            size="sm"
                            className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
                          >
                            {sendingEmailInvite ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Mail className="w-4 h-4 mr-2" />
                                Send Invitation
                              </>
                            )}
                          </Button>

                          {lastInviteLink && (
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                              <p className="text-xs text-green-800 mb-2 font-medium">
                                ✅ Invitation created! Share this link:
                              </p>
                              <div className="flex gap-2">
                                <Input 
                                  value={lastInviteLink} 
                                  readOnly 
                                  className="text-xs bg-white"
                                />
                                <Button size="sm" variant="outline" onClick={copyInviteLink}>
                                  <Copy className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* IDEO Attribution */}
            <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                  <div className="text-sm text-gray-700">
                    <strong>Powered by IDEO</strong>
                    <p className="mt-1">
                      This 7-day sprint follows IDEO's proven design thinking methodology for human-centered innovation.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpactLabDashboard;
